import type { ActiveStage, JourneyStage, StageDimensions } from './journey-context';

export type { StageDimensions };

/**
 * What each stage's Start is called. Named one by one, because these predate the funnel and are the names
 * already in use in reporting.
 */
const stageStartEventNames: Record<JourneyStage, string> = {
	Visit: 'SiteVisited',
	CreateOrganization: 'OrganizationCreationStarted',
	CreateForm: 'FormCreationStarted',
	ActivateForm: 'FormShared',
	ConvertToPaid: 'UpgradeIntentClicked',
	ChangePlan: 'PlanChangeStarted',
	SignupUser: 'UserSignupStarted',
	SetUpPayments: 'PaymentSetupStarted',
	RecoverPayment: 'PaymentRecoveryStarted',
	ContactSales: 'SalesContactStarted'
};

export function startEventName(stage: JourneyStage): string {
	return stageStartEventNames[stage];
}

/**
 * What a stage's cancellation is called. Must match `JourneyStageCancelledCustomEvent` on the server.
 */
export function cancelEventName(stage: JourneyStage): string {
	return `${stage}Cancelled`;
}

/**
 * The dimension that marks an event as a customer journey signal, which is how Bronze ingestion recognizes
 * one without knowing every event name. Matches `JourneySignal.PropertyName` on the server.
 */
export const JOURNEY_SIGNAL_DIMENSION = 'IsUserJourneySignal';

export const StepRoles = {
	Start: 'Start',
	Step: 'Step',
	Complete: 'Complete',
	Cancel: 'Cancel'
} as const;

export type StepRole = typeof StepRoles[keyof typeof StepRoles];

/**
 * The keys the Click Analytics plugin writes into `item.data`. That object is merged into
 * `customDimensions` at envelope time and takes precedence, so a journey dimension sharing one of
 * these names would be silently lost. Never emit a dimension named after one of these.
 */
export const RESERVED_EVENT_DATA_KEYS: readonly string[] = [
	'baseTypeSource',
	'uri',
	'pageType',
	'properties',
	'actionType',
	'behavior',
	'clickCoordinates',
	'content',
	'targetUri',
	'timeToAction',
	'refUri',
	'pageName',
	'parentId'
];

/**
 * The dimension a `data-cf-` attribute asks for: `data-cf-field-type` becomes `FieldType`. Returns an empty
 * name for an attribute holding anything the convention does not cover, which contributes no dimension.
 */
export function dimensionName(attribute: string): string {
	return attribute
		.slice('data-cf-'.length)
		.split('-')
		.filter(part => /^[A-Za-z0-9]+$/.test(part))
		.map(part => part.charAt(0).toUpperCase() + part.slice(1))
		.join('');
}

/**
 * Whether a dimension name would be lost to one of the plugin's own keys, compared without regard to case
 * because the collision is the plugin's to make and it does not have to spell it ours.
 */
export function isReservedDimension(name: string): boolean {
	return RESERVED_EVENT_DATA_KEYS.some(reserved => reserved.toLowerCase() === name.toLowerCase());
}

export type JourneySignalContext = {
	organizationId?: string;
	userId?: string;
	visitorId?: string;
	formId?: string;
	experimentFlags?: string;
};

export type JourneySignal = {
	name: string;
	dimensions: Record<string, string>;
};

function assign(dimensions: Record<string, string>, name: string, value: string | undefined): void {
	if (!value)
		return;
	dimensions[name] = value;
}

function openEnvelope(stage: JourneyStage, stepRole: StepRole): Record<string, string> {
	return {
		[JOURNEY_SIGNAL_DIMENSION]: 'True',
		Stage: stage,
		StepRole: stepRole
	};
}

function openStageEnvelope(active: ActiveStage, stepRole: StepRole): Record<string, string> {
	const dimensions = openEnvelope(active.stage, stepRole);

	assign(dimensions, 'StageCorrelationId', active.stageCorrelationId);
	assign(dimensions, 'Variant', active.variant);

	return dimensions;
}

function identify(dimensions: Record<string, string>, context: JourneySignalContext): void {
	assign(dimensions, 'OrganizationId', context.organizationId);
	assign(dimensions, 'UserId', context.userId);
	assign(dimensions, 'VisitorId', context.visitorId);
	assign(dimensions, 'FormId', context.formId);
	assign(dimensions, 'ExperimentFlags', context.experimentFlags);
}

function buildSignal(
	name: string,
	stepRole: StepRole,
	active: ActiveStage,
	context: JourneySignalContext
): JourneySignal {
	const dimensions = openStageEnvelope(active, stepRole);

	assign(dimensions, 'EntryPoint', active.entryPoint);
	assign(dimensions, 'Detail', active.detail);
	identify(dimensions, context);

	return { name, dimensions };
}

export function buildStartSignal(active: ActiveStage, context: JourneySignalContext = {}): JourneySignal {
	return buildSignal(startEventName(active.stage), StepRoles.Start, active, context);
}

/**
 * The dimensions carried by an intermediary event, inherited wholesale from the open stage. The step
 * contributes only its own name — it never has to know which stage it belongs to.
 */
export function buildStepSignal(stepName: string, active: ActiveStage, context: JourneySignalContext = {}): JourneySignal {
	const signal = buildSignal(stepName, StepRoles.Step, active, context);
	signal.dimensions.StepName = stepName;
	return signal;
}

/**
 * The dimensions carried by a step whose stage turns out not to be open: emitted with no correlation id
 * rather than dropped. Only a step that names its own stage can be built this way. Anything the step
 * declares about itself still travels — with no stage to have inherited it from, the step is the only
 * thing that knows it.
 */
export function buildOrphanStepSignal(stepName: string, stage: JourneyStage, context: JourneySignalContext = {}, declared: StageDimensions = {}): JourneySignal {
	const dimensions = openEnvelope(stage, StepRoles.Step);
	dimensions.StepName = stepName;

	assign(dimensions, 'Variant', declared.variant);
	assign(dimensions, 'EntryPoint', declared.entryPoint);
	assign(dimensions, 'Detail', declared.detail);
	identify(dimensions, context);

	return { name: stepName, dimensions };
}

/**
 * The dimensions carried by the event that retracts a stage. The stage's own dimensions are left off, to
 * match what the server emits — they are already on the Start it pairs with.
 */
export function buildCancelSignal(active: ActiveStage, reason: string, context: JourneySignalContext = {}): JourneySignal {
	const dimensions = openStageEnvelope(active, StepRoles.Cancel);

	assign(dimensions, 'CancelReason', reason);
	identify(dimensions, context);

	return { name: cancelEventName(active.stage), dimensions };
}
